<template>
    <hox-modal @close="close">
        <hox-loading-layer v-if="isUpdatingMetadataField" />
        <DeleteMetadataFieldModal
            v-if="deleteMetadataFieldModalIsVisible"
            :field-id="fieldId"
            :column-name="columnName"
            @close="toggleDeleteMetadataFieldModalIsVisible"
            @metadataFieldDeleted="close"
        />
        <template #header>
            Edit the "
            <em>{{ columnName }}</em>
            " column
        </template>
        <MetadataFieldForm
            :has-error-submitting-form="hasErrorUpdatingMetadataField"
            :initial-form-data="{ columnName }"
            :show-validation-errors="true"
            :error="formError"
            @formDataUpdate="setFormData"
            @hasValidationErrorsUpdate="setHasValidationErrors"
            @submit="updateMetadataField"
        />
        <template #footer>
            <div class="edit-metadata-field-modal__buttons-container">
                <Button ghost type="error" @click="toggleDeleteMetadataFieldModalIsVisible">Delete</Button>
                <Button :disabled="hasValidationErrors" type="primary" @click="updateMetadataField">Save</Button>
            </div>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type ValidationError from "hoxton-shared/errors/ValidationError";

import DeleteMetadataFieldModal from "@/components/Metadata/DeleteMetadataFieldModal.vue";
import MetadataFieldForm from "@/components/Metadata/MetadataFieldForm.vue";
import { updateMetadataField } from "@/services/Metadata";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";

export default defineComponent({
    components: {
        DeleteMetadataFieldModal,
        MetadataFieldForm
    },

    props: {
        columnName: {
            required: true,
            type: String
        },

        fieldId: {
            type: String,
            required: true
        }
    },

    emits: ["close"],

    data() {
        return {
            deleteMetadataFieldModalIsVisible: false,
            formData: {} as { columnName: string },
            hasErrorUpdatingMetadataField: false,
            hasValidationErrors: false,
            isUpdatingMetadataField: false,
            formError: null as Error | ValidationError | null
        };
    },

    methods: {
        close() {
            this.$emit("close");
        },

        setFormData(formData: { columnName: string }) {
            this.formData = formData;
            this.formError = null;
            this.hasErrorUpdatingMetadataField = false;
        },

        setHasValidationErrors(hasValidationErrors: boolean) {
            this.hasValidationErrors = hasValidationErrors;
        },

        toggleDeleteMetadataFieldModalIsVisible() {
            this.deleteMetadataFieldModalIsVisible = !this.deleteMetadataFieldModalIsVisible;
        },

        async updateMetadataField() {
            if (!this.hasValidationErrors) {
                this.hasErrorUpdatingMetadataField = false;
                this.isUpdatingMetadataField = true;
                try {
                    const updatedMetadataField = await updateMetadataField(this.fieldId, this.formData.columnName);
                    this.$store.dispatch(DeliverableLibraryAction.SetMetadataFieldsByFieldId, updatedMetadataField);

                    if (this.columnName !== updatedMetadataField.name) {
                        this.$snackbar.success(
                            `The column "${this.columnName}" has been renamed to "${updatedMetadataField.name}"`
                        );
                    }
                } catch (err) {
                    this.formError = err as Error | ValidationError;
                    this.hasErrorUpdatingMetadataField = true;
                    return;
                } finally {
                    this.isUpdatingMetadataField = false;
                }
                this.close();
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.edit-metadata-field-modal__buttons-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
}
</style>
